(() => {
  if (customElements.get('fabric-sort-menu')) return;
  const modes = {'weight-desc':'重い順','weight-asc':'軽い順','thickness-desc':'厚い順','thickness-asc':'薄い順'};
  const cache = new Map();
  function numeric(raw, kind) {
    const s = (raw || '').normalize('NFKC').replace(/\s/g, '');
    const range = s.match(/^(\d+(?:\.\d+)?)(?:[〜～~–−-](\d+(?:\.\d+)?))?/);
    if (!range) return null;
    const rest = s.slice(range[0].length);
    if (kind === 'weight' && !/^g\/(?:m2|m²|㎡)/i.test(rest)) return null;
    if (kind === 'thickness' && rest !== '' && !/^mm(?:$|[（(])/.test(rest)) return null;
    const value = Math.min(Number(range[1]), Number(range[2] || range[1]));
    return value > 0 ? value : null;
  }
  function readData(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root = doc.querySelector('[data-fabric-sort-data]');
    if (!root) throw Error('商品データを読み込めませんでした');
    return {pages: Number(root.dataset.pages) || 1, items: [...root.querySelectorAll('[data-fabric-sort-item]')].flatMap(row =>
      [...row.querySelectorAll('product-card')].map(card => ({
        html: card.outerHTML, handle: row.dataset.handle,
        weight: numeric(row.dataset.weight, 'weight'), thickness: numeric(row.dataset.thickness, 'thickness')
      }))) };
  }
  customElements.define('fabric-sort-menu', class extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      this.grid = this.section.querySelector('product-list');
      this.status = this.querySelector('[data-fabric-sort-status]');
      this.abort = new AbortController();
      this.addEventListener('click', e => {
        const b = e.target.closest('[data-fabric-sort]');
        if (b) this.run(b.dataset.fabricSort, 1);
      }, {signal: this.abort.signal});
      const url = new URL(location.href), mode = url.searchParams.get('fabric_sort');
      if (modes[mode] && this.grid) this.run(mode, Number(url.searchParams.get('fabric_page')) || 1);
    }
    disconnectedCallback() {this.abort?.abort(); this.navigation?.forEach(n => n.remove());}
    async load() {
      const base = new URL(location.href);
      ['fabric_sort','fabric_page','page','section_id','sort_by'].forEach(k => base.searchParams.delete(k));
      base.searchParams.set('sort_by','title-ascending');
      base.searchParams.set('section_id','fabric-sort-data');
      base.hash = '';
      const key = base.toString();
      if (cache.has(key)) return cache.get(key);
      const fetchPage = async page => {
        const u = new URL(base); u.searchParams.set('page',page);
        const r = await fetch(u, {signal: this.abort.signal});
        if (!r.ok) throw Error('商品データを読み込めませんでした');
        return readData(await r.text());
      };
      const first = await fetchPage(1), items = first.items;
      // Keep concurrent downloads bounded and only cache a complete collection.
      for (let p = 2; p <= first.pages; p += 2) {
        const batch = await Promise.all([p,p+1].filter(n=>n<=first.pages).map(fetchPage));
        batch.forEach(b => items.push(...b.items));
      }
      if (cache.size >= 3) cache.delete(cache.keys().next().value);
      cache.set(key,items); return items;
    }
    async run(mode,page) {
      if (!modes[mode] || !this.grid) return;
      const turn = this.turn = (this.turn || 0) + 1;
      this.status.textContent = '全商品を並び替えています…';
      this.closest('sort-tabs').querySelector('[data-sort-current]').textContent = modes[mode] + 'に並び替え中…';
      this.grid.setAttribute('aria-busy','true');
      try {
        const items = await (this.loading ||= this.load().catch(e=>{this.loading=null;throw e;}));
        if (!this.isConnected || turn !== this.turn) return;
        const [kind, direction] = mode.split('-');
        this.items = items.map((item,index)=>({...item,index})).sort((a,b)=>{
          if (a[kind] === null) return b[kind] === null ? a.index-b.index : 1;
          if (b[kind] === null) return -1;
          return (a[kind]-b[kind])*(direction==='asc'?1:-1) || a.index-b.index;
        });
        this.mode = mode;
        this.section.querySelectorAll('.sort-tabs__tab').forEach(b=>{
          const active = b.dataset.fabricSort === mode;
          b.classList.toggle('sort-tabs__tab--active',active);b.setAttribute('aria-pressed',String(active));
        });
        this.section.setAttribute('data-fabric-sort-active','');
        this.paint(page);
        this.closest('sort-tabs').querySelector('[data-sort-current]').textContent = modes[mode];
        const disclosure = this.closest('details');
        if (disclosure?.open) {disclosure.open = false;disclosure.querySelector('summary').focus({preventScroll:true});}
      } catch (e) {
        if (e.name !== 'AbortError' && turn === this.turn) {this.status.textContent = '読み込めませんでした。もう一度ボタンを押してください。';this.closest('details').open=true;this.closest('sort-tabs').querySelector('[data-sort-current]').textContent='再選択してください';}
      } finally {if (turn === this.turn) this.grid.removeAttribute('aria-busy');}
    }
    paint(page) {
      const size = Number(this.dataset.pageSize) || 24, pages = Math.max(1,Math.ceil(this.items.length/size));
      page = Math.min(pages, Math.max(1,Math.floor(page) || 1));
      const template = document.createElement('template');
      template.innerHTML = this.items.slice((page-1)*size,page*size).map(x=>x.html).join('');
      template.content.querySelectorAll('product-card').forEach((card,i)=>{
        card.removeAttribute('reveal-on-scroll');
        const item = this.items[(page-1)*size+i];
        card.dataset.sortWeight = item.weight ?? ''; card.dataset.sortThickness = item.thickness ?? '';
      });
      this.grid.replaceChildren(template.content);
      this.navigation?.forEach(n=>n.remove()); this.navigation = [];
      const nav = () => {
        const n = document.createElement('nav');n.className='fabric-sort-pagination';n.setAttribute('aria-label','並び替えた商品のページ');
        for (const [label,target,disabled] of [['前へ',page-1,page===1],[`${page} / ${pages}`,null,false],['次へ',page+1,page===pages]]) {
          const e = document.createElement(target===null?'span':'button');e.textContent=label;
          if(target!==null){e.type='button';e.disabled=disabled;e.addEventListener('click',()=>{this.paint(target);this.grid.scrollIntoView({block:'start',behavior:'smooth'});});}
          n.append(e);
        }
        this.navigation.push(n);return n;
      };
      this.grid.before(nav());this.grid.after(nav());
      this.status.textContent = `${modes[this.mode]}で表示中`;
      const url = new URL(location.href);url.searchParams.set('fabric_sort',this.mode);url.searchParams.set('fabric_page',page);url.searchParams.delete('page');url.searchParams.delete('section_id');
      history.replaceState(history.state,'',url);
    }
  });
  document.addEventListener('facet:update',event=>{
    const current = new URL(location.href).searchParams.get('fabric_sort');
    if (event.detail.fabricSortReset) {event.detail.url.searchParams.delete('fabric_sort');event.detail.url.searchParams.delete('fabric_page');}
    else if (modes[current]) {event.detail.url.searchParams.set('fabric_sort',current);event.detail.url.searchParams.delete('fabric_page');event.detail.url.searchParams.delete('page');}
  },true);
})();
